'use client';

import { useCallback, useEffect, useState } from 'react';
import { fetchAPI } from '@/core/api/client';

export type SpecRow = { name: string; value: string };
export type SpecTemplates = Record<string, SpecRow[]>;

// Templates used to live only in this browser's localStorage. They now live in the store config
// (site_config.spec_templates, /admin/spec-templates) so every admin and device sees the same set
// and a catalog import can seed them. Anything still saved locally is uploaded once, then dropped.
const LEGACY_STORAGE_KEY = 'shopstag_spec_templates';

// JSONB doesn't keep key order — list templates alphabetically wherever they are offered.
function sorted(templates: SpecTemplates): SpecTemplates {
    return Object.fromEntries(Object.entries(templates).sort(([a], [b]) => a.localeCompare(b, 'pl')));
}

function readLegacyTemplates(): SpecTemplates {
    try {
        const saved = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || '{}');
        return Object.fromEntries(
            Object.entries(saved as Record<string, any[]>).map(([name, rows]) => [
                name,
                (Array.isArray(rows) ? rows : []).map(r => ({ name: String(r?.name ?? r?.key ?? ''), value: String(r?.value ?? '') })),
            ]),
        );
    } catch {
        return {};
    }
}

export function useSpecTemplates(enabled = true) {
    const [templates, setTemplates] = useState<SpecTemplates>({});

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        (async () => {
            try {
                let loaded: SpecTemplates = (await fetchAPI('/admin/spec-templates', { cache: 'no-store' })) || {};
                const legacy = readLegacyTemplates();
                const missing = Object.keys(legacy).filter(name => !(name in loaded));
                if (missing.length > 0) {
                    const merged = { ...loaded, ...Object.fromEntries(missing.map(name => [name, legacy[name]])) };
                    loaded = (await fetchAPI('/admin/spec-templates', { method: 'PUT', body: JSON.stringify(merged) })) || merged;
                }
                try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* storage unavailable */ }
                if (!cancelled) setTemplates(sorted(loaded));
            } catch (err) {
                console.error('Spec templates load error:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [enabled]);

    const saveTemplate = useCallback(async (name: string, rows: SpecRow[]) => {
        const updated = { ...templates, [name]: rows };
        const saved = await fetchAPI('/admin/spec-templates', { method: 'PUT', body: JSON.stringify(updated) });
        setTemplates(sorted(saved || updated));
    }, [templates]);

    return { templates, saveTemplate };
}
